import { useState } from 'react';
import type { FormEvent } from 'react';
import type { ShiftStatus } from '@dental-staffing-platform/shared';
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Pagination,
  Row,
  Select,
  SkeletonList,
  Stack,
} from '@dental-staffing-platform/ui';
import { adminApi } from '../lib/admin-client.js';
import { useAsync } from '../lib/useAsync.js';
import { humanize } from '../lib/format.js';

export function Operations() {
  const [locationPage, setLocationPage] = useState(1);
  const [locationSearch, setLocationSearch] = useState('');
  const [locationActive, setLocationActive] = useState('all');
  const [shiftPage, setShiftPage] = useState(1);
  const [shiftSearch, setShiftSearch] = useState('');
  const [shiftView, setShiftView] = useState('active');
  const [shiftStatus, setShiftStatus] = useState('all');
  const state = useAsync(
    () =>
      Promise.all([
        adminApi.groups.list(),
        adminApi.locations.page({
          page: locationPage,
          pageSize: 10,
          ...(locationSearch.trim() ? { search: locationSearch.trim() } : {}),
          ...(locationActive !== 'all' ? { active: locationActive === 'active' } : {}),
        }),
        adminApi.shifts.page({
          page: shiftPage,
          pageSize: 10,
          ...(shiftSearch.trim() ? { search: shiftSearch.trim() } : {}),
          ...(shiftView !== 'all' ? { view: shiftView as 'active' | 'history' } : {}),
          ...(shiftStatus !== 'all' ? { status: shiftStatus as ShiftStatus } : {}),
        }),
      ]),
    [locationPage, locationSearch, locationActive, shiftPage, shiftSearch, shiftView, shiftStatus]
  );
  const [groupName, setGroupName] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const createGroup = async (event: FormEvent) => {
    event.preventDefault();
    setBusy('group');
    setActionError(null);
    try {
      await adminApi.groups.create(groupName.trim());
      setGroupName('');
      state.reload();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Could not create group');
    } finally {
      setBusy(null);
    }
  };

  const toggleLocation = async (id: string, isActive: boolean) => {
    setBusy(id);
    setActionError(null);
    try {
      await adminApi.locations.update(id, { isActive: !isActive });
      state.reload();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Could not update location');
    } finally {
      setBusy(null);
    }
  };

  const cancelShift = async (id: string) => {
    setBusy(id);
    setActionError(null);
    try {
      await adminApi.shifts.cancel(id, 'Cancelled by platform administrator');
      state.reload();
    } catch (error) {
      setActionError(error instanceof Error ? error.message : 'Could not cancel shift');
    } finally {
      setBusy(null);
    }
  };

  if (state.loading) return <SkeletonList count={5} />;
  if (state.error || !state.data) {
    return (
      <Card>
        <span className="dsp-field__error">{state.error ?? 'Could not load operations'}</span>
      </Card>
    );
  }
  const [groups, locations, shifts] = state.data;

  return (
    <div>
      <PageHeader title="Operations" subtitle="Groups, locations, and shift exceptions" />
      {actionError ? (
        <Card style={{ marginBottom: 12 }}>
          <span className="dsp-field__error">{actionError}</span>
        </Card>
      ) : null}

      <Card style={{ marginBottom: 16 }}>
        <form onSubmit={(event) => void createGroup(event)}>
          <Row gap={10} style={{ alignItems: 'end', flexWrap: 'wrap' }}>
            <Field label="New practice group">
              <Input
                required
                value={groupName}
                onChange={(event) => setGroupName(event.target.value)}
                placeholder="Bright Smiles Dental"
              />
            </Field>
            <Button type="submit" disabled={busy === 'group' || !groupName.trim()}>
              {busy === 'group' ? 'Creating…' : 'Create group'}
            </Button>
          </Row>
        </form>
        <Row gap={8} style={{ marginTop: 12, flexWrap: 'wrap' }}>
          {groups.map((group) => (
            <Badge key={group.id} tone={group.status === 'active' ? 'success' : 'neutral'}>
              {group.name} · {group.status}
            </Badge>
          ))}
        </Row>
      </Card>

      <h2 style={{ fontSize: '1.1rem', marginBottom: 10 }}>Locations</h2>
      <Card style={{ marginBottom: 12 }}>
        <Row gap={12} style={{ alignItems: 'end', flexWrap: 'wrap' }}>
          <Field label="Search locations">
            <Input
              type="search"
              value={locationSearch}
              placeholder="Name, city, state, or ZIP"
              onChange={(event) => {
                setLocationSearch(event.target.value);
                setLocationPage(1);
              }}
            />
          </Field>
          <Field label="Location status">
            <Select
              value={locationActive}
              onChange={(event) => {
                setLocationActive(event.target.value);
                setLocationPage(1);
              }}
            >
              <option value="all">All locations</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </Select>
          </Field>
        </Row>
      </Card>
      {locations.items.length === 0 ? (
        <EmptyState
          title="No locations"
          description="Create locations from the office workspace."
        />
      ) : (
        <Stack gap={10}>
          {locations.items.map((location) => (
            <Card key={location.id}>
              <Row style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <strong>{location.name}</strong>
                  <div className="dsp-muted" style={{ fontSize: '0.86rem' }}>
                    {location.city}, {location.state} · {location.timezone}
                  </div>
                </div>
                <Row gap={8}>
                  <Badge tone={location.isActive ? 'success' : 'neutral'}>
                    {location.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy === location.id}
                    onClick={() => void toggleLocation(location.id, location.isActive)}
                  >
                    {location.isActive ? 'Deactivate' : 'Activate'}
                  </Button>
                </Row>
              </Row>
            </Card>
          ))}
          <Pagination
            {...locations}
            itemLabel="locations"
            disabled={state.loading}
            onPageChange={setLocationPage}
          />
        </Stack>
      )}

      <h2 style={{ fontSize: '1.1rem', margin: '22px 0 10px' }}>Shifts</h2>
      <Card style={{ marginBottom: 12 }}>
        <Row gap={12} style={{ alignItems: 'end', flexWrap: 'wrap' }}>
          <Field label="Search shifts">
            <Input
              type="search"
              value={shiftSearch}
              placeholder="Location, group, role, or notes"
              onChange={(event) => {
                setShiftSearch(event.target.value);
                setShiftPage(1);
              }}
            />
          </Field>
          <Field label="Timeframe">
            <Select
              value={shiftView}
              onChange={(event) => {
                setShiftView(event.target.value);
                setShiftPage(1);
              }}
            >
              <option value="all">All shifts</option>
              <option value="active">Active</option>
              <option value="history">History</option>
            </Select>
          </Field>
          <Field label="Status">
            <Select
              value={shiftStatus}
              onChange={(event) => {
                setShiftStatus(event.target.value);
                setShiftPage(1);
              }}
            >
              <option value="all">All statuses</option>
              <option value="open">Open</option>
              <option value="pending_confirmation">Pending confirmation</option>
              <option value="filled">Filled</option>
              <option value="in_progress">In progress</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </Select>
          </Field>
        </Row>
      </Card>
      {shifts.items.length === 0 ? (
        <EmptyState title="No matching shifts" description="Adjust the shift filters." />
      ) : (
        <Stack gap={10}>
          {shifts.items.map((shift) => (
            <Card key={shift.id}>
              <Row style={{ justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
                <div>
                  <strong>{humanize(shift.roleRequired)}</strong>
                  <div className="dsp-muted" style={{ fontSize: '0.86rem' }}>
                    {shift.locationName} · {new Date(shift.startsAt).toLocaleString()}
                  </div>
                </div>
                <Row gap={8}>
                  <Badge tone={shift.status === 'cancelled' ? 'neutral' : 'info'}>
                    {humanize(shift.status)}
                  </Badge>
                  {!['cancelled', 'completed'].includes(shift.status) ? (
                    <Button
                      size="sm"
                      variant="danger"
                      disabled={busy === shift.id}
                      onClick={() => void cancelShift(shift.id)}
                    >
                      Cancel
                    </Button>
                  ) : null}
                </Row>
              </Row>
            </Card>
          ))}
          <Pagination
            {...shifts}
            itemLabel="shifts"
            disabled={state.loading}
            onPageChange={setShiftPage}
          />
        </Stack>
      )}
    </div>
  );
}
